// src/pages/accounts/TallyExport.jsx

import { useState } from "react";
import { db } from "../../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useCompany } from "../../context/CompanyContext";
import {
  generateSalesXML,
  generateExpenseXML,
  generatePurchaseXML,
  generateAllXML,
  downloadXML,
} from "../../services/tallyExportService";

const EXPORT_TYPES = [
  { key: "all",      label: "All Vouchers",       icon: "📦", desc: "Sales + Expenses + Purchases" },
  { key: "sales",    label: "Sales Vouchers",      icon: "🧾", desc: "From Invoices" },
  { key: "expenses", label: "Payment Vouchers",    icon: "💸", desc: "Expenses + Transport" },
  { key: "purchase", label: "Purchase Vouchers",   icon: "🛒", desc: "Purchase Orders" },
];

export default function TallyExport() {
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";
  const { company } = useCompany();

  const [exportType, setExportType] = useState("all");
  const [dateFrom,   setDateFrom]   = useState("");
  const [dateTo,     setDateTo]     = useState("");
  const [loading,    setLoading]    = useState(false);
  const [preview,    setPreview]    = useState(null); // { invoices, expenses, pos }
  const [previewing, setPreviewing] = useState(false);

  // ── Date filter helper ──────────────────────────────────
  const inRange = (item) => {
  if (!dateFrom && !dateTo) return true;
  // Handle Firestore Timestamp, plain Date, ISO string, epoch
  const ts = item.invoice_date?.seconds || item.created_at?.seconds;
  const d  = ts
    ? new Date(ts * 1000)
    : new Date(item.invoice_date || item.created_at || 0);
  if (isNaN(d)) return true; // unparseable → include it
  if (dateFrom && d < new Date(dateFrom)) return false;
  if (dateTo   && d > new Date(dateTo + "T23:59:59")) return false;
  return true;
};

  // ── Fetch data for preview ──────────────────────────────
  const fetchPreview = async () => {
    if (!companyId) { alert("Company not loaded"); return; }
    setPreviewing(true);
    try {
      const [invSnap, expSnap, transSnap, poSnap] = await Promise.all([
        getDocs(query(collection(db, "invoices"),
          where("company_id", "==", companyId))),
        getDocs(query(collection(db, "financial_transactions"),
          where("company_id", "==", companyId),
          where("type", "==", "expense"))),
        getDocs(query(collection(db, "financial_transactions"),
          where("company_id", "==", companyId),
          where("type", "==", "transport"))),
        getDocs(query(collection(db, "purchase_orders"),
          where("company_id", "==", companyId))),
      ]);

      const invoices  = invSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(inRange);
      const expenses  = [
        ...expSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        ...transSnap.docs.map(d => ({ id: d.id, ...d.data() })),
      ].filter(inRange);
      const pos = poSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(po => po.grn_locked === true) // only completed POs
        .filter(inRange);

      setPreview({ invoices, expenses, pos });
    } catch (err) {
      console.error("Preview error:", err);
      alert("Failed to fetch data");
    } finally { setPreviewing(false); }
  };

  // ── Export ──────────────────────────────────────────────
  const handleExport = async () => {
    if (!preview) { alert("Click Preview first"); return; }
    if (!company) { alert("Company profile not loaded"); return; }

    const config = company; // company document has tally_* fields
    const { invoices, expenses, pos } = preview;

    setLoading(true);
    try {
      let xml      = "";
      let filename = "";
      const date   = new Date().toISOString().slice(0, 10);

      switch (exportType) {
        case "sales":
          xml      = generateSalesXML(invoices, company, config);
          filename = `tally_sales_${date}.xml`;
          break;
        case "expenses":
          xml      = generateExpenseXML(expenses, company, config);
          filename = `tally_payments_${date}.xml`;
          break;
        case "purchase":
          xml      = generatePurchaseXML(pos, company, config);
          filename = `tally_purchases_${date}.xml`;
          break;
        default:
          xml      = generateAllXML(invoices, expenses, pos, company, config);
          filename = `tally_all_${date}.xml`;
      }

      downloadXML(xml, filename);
    } catch (err) {
      console.error("Export error:", err);
      alert("Export failed: " + err.message);
    } finally { setLoading(false); }
  };

  const counts = preview ? {
    invoices: preview.invoices.filter(i => Number(i.total_amount || 0) > 0).length,
    expenses: preview.expenses.filter(e => Number(e.amount || 0) > 0).length,
    pos:      preview.pos.filter(p => Number(p.grand_total || p.total_amount || 0) > 0).length,
  } : null;

  return (
    <div className="p-4 bg-gray-50 min-h-screen max-w-2xl mx-auto">
      <h1 className="text-xl font-bold text-slate-800 mb-1">Tally Export</h1>
      <p className="text-sm text-slate-500 mb-5">
        Export vouchers for Tally ERP 9 / TallyPrime import
      </p>

      {/* Company check */}
      {company && (!company.state_code || !company.gst_number) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-xs text-amber-700">
          ⚠ <strong>Company Profile incomplete</strong> — GST number or State missing.
          <a href="/company-profile" className="underline ml-1">Fill Company Profile →</a>
        </div>
      )}

      <div className="space-y-4">

        {/* EXPORT TYPE */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
          <label className="text-xs font-semibold text-slate-500 uppercase block mb-3">
            Export Type
          </label>
          <div className="grid grid-cols-2 gap-2">
            {EXPORT_TYPES.map(t => (
              <button key={t.key} onClick={() => setExportType(t.key)}
                className={`p-3 rounded-xl border text-left transition ${
                  exportType === t.key
                    ? "border-slate-800 bg-slate-800 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}>
                <div className="text-lg mb-0.5">{t.icon}</div>
                <div className="text-sm font-semibold">{t.label}</div>
                <div className={`text-xs mt-0.5 ${exportType === t.key ? "text-slate-300" : "text-slate-400"}`}>
                  {t.desc}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* DATE RANGE */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
          <label className="text-xs font-semibold text-slate-500 uppercase block mb-3">
            Date Range <span className="font-normal normal-case text-slate-400">(optional — leave blank for all)</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 block mb-1">From</label>
              <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPreview(null); }}
                className="w-full border border-slate-200 p-2.5 rounded-xl text-sm outline-none" />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">To</label>
              <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPreview(null); }}
                className="w-full border border-slate-200 p-2.5 rounded-xl text-sm outline-none" />
            </div>
          </div>
        </div>

        {/* PREVIEW BUTTON */}
        <button onClick={fetchPreview} disabled={previewing}
          className="w-full py-3 rounded-xl border-2 border-slate-300 text-slate-700 font-semibold text-sm hover:bg-slate-50 disabled:opacity-40 transition">
          {previewing ? "Loading..." : "🔍 Preview Data"}
        </button>

        {/* PREVIEW COUNTS */}
        {counts && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Data Preview</p>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "Invoices",  count: counts.invoices, icon: "🧾", color: "text-blue-600" },
                { label: "Expenses",  count: counts.expenses, icon: "💸", color: "text-red-600" },
                { label: "Purchases", count: counts.pos,      icon: "🛒", color: "text-purple-600" },
              ].map(({ label, count, icon, color }) => (
                <div key={label} className="text-center p-3 bg-slate-50 rounded-xl">
                  <div className="text-xl">{icon}</div>
                  <div className={`text-2xl font-black mt-1 ${color}`}>{count}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{label}</div>
                </div>
              ))}
            </div>

            {(counts.invoices + counts.expenses + counts.pos) === 0 && (
              <p className="text-center text-slate-400 text-sm mt-3">
                No data found for selected range.
              </p>
            )}
          </div>
        )}

        {/* EXPORT BUTTON */}
        {counts && (counts.invoices + counts.expenses + counts.pos) > 0 && (
          <button onClick={handleExport} disabled={loading}
            className="w-full py-3.5 rounded-xl bg-slate-800 hover:bg-slate-900 text-white font-semibold text-sm disabled:opacity-50 transition">
            {loading ? "Generating XML..." : "⬇ Download Tally XML File"}
          </button>
        )}

        {/* INSTRUCTIONS */}
        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 text-xs text-blue-700 space-y-2">
          <p className="font-bold text-sm text-blue-800">📋 How to import in Tally</p>
          <ol className="space-y-1.5 list-decimal list-inside">
            <li>Download the XML file above</li>
            <li>Open <strong>Tally ERP 9</strong> or <strong>TallyPrime</strong></li>
            <li>Go to <strong>Gateway of Tally → Import Data → Vouchers</strong></li>
            <li>Select the downloaded XML file</li>
            <li>Tally will import all vouchers automatically</li>
          </ol>
          <div className="mt-2 pt-2 border-t border-blue-100">
            <p className="font-semibold text-blue-800">⚠ Before importing:</p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>Verify ledger names in <a href="/company-profile" className="underline">Company Profile → Tally Mapping</a> match your Tally exactly</li>
              <li>Client/Vendor ledgers must exist in Tally before import</li>
              <li>Take a backup of Tally data before first import</li>
            </ul>
          </div>
        </div>

      </div>
    </div>
  );
}
